/**
 * AI 이용권(리포트/계약서/제안서) 구매 페이지 — 웹 전용 (/ai-credits/:toolType)
 * tech_spec.md 8-3절. 앱에서는 이 경로로 들어오지 않고 외부 브라우저로 위임됨(AICreditGate.tsx 참조).
 * ?code= 쿼리: 앱에서 발급한 원타임 코드로 자동 로그인 (appBridge.openAICreditsExternally)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { CreditCard, CheckCircle, ArrowLeft, Zap } from 'lucide-react';
import { aiCreditsAPI, AIToolType, AIPackageType } from '../api/aiCreditsAPI';

declare global { interface Window { AUTHNICE: any; } }

const TOOL_LABEL: Record<string, string> = {
  report: 'AI 전문가 분석 리포트',
  contract: 'AI 계약서 초안',
  proposal: 'AI 제안서 도우미',
};

const S = {
  page: { minHeight: '100vh', background: '#080808', color: '#fff', fontFamily: "-apple-system,'Pretendard',sans-serif", padding: '32px 24px' } as React.CSSProperties,
  container: { maxWidth: 480, margin: '0 auto' } as React.CSSProperties,
  card: { background: '#0d0d0d', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: 24, marginBottom: 16 } as React.CSSProperties,
};

const AICreditsPage: React.FC = () => {
  const { toolType } = useParams<{ toolType: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [balance, setBalance] = useState<number | null>(null);
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);

  const label = (toolType && TOOL_LABEL[toolType]) || 'AI 기능';
  const isValidTool = toolType === 'report' || toolType === 'contract' || toolType === 'proposal';

  const loadBalance = useCallback(async () => {
    if (!isValidTool) return;
    try {
      const b = await aiCreditsAPI.getBalance(toolType as AIToolType);
      setBalance(b);
    } catch (e) { console.error('[AICredits] 잔여 조회 오류:', e); }
  }, [toolType, isValidTool]);

  // 앱에서 넘어온 원타임 코드 → JWT 교환
  useEffect(() => {
    const code = searchParams.get('code');
    if (code) {
      fetch(`${process.env.REACT_APP_API_BASE_URL || 'https://api.freematch.uk/api'}/membership/exchange-code`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ code }),
      })
        .then((r) => r.json())
        .then((d) => {
          if (d.success) {
            localStorage.setItem('token', d.data.accessToken);
            setSearchParams((prev) => { prev.delete('code'); return prev; }, { replace: true });
          }
        })
        .catch((e) => console.error('[AICredits] 코드 교환 오류:', e))
        .finally(loadBalance);
    } else {
      loadBalance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 나이스페이 결제 리다이렉트 처리 — 백엔드 returnUrl 콜백에서 이미 승인/충전을 끝내고 돌아온 상태
  useEffect(() => {
    const payment = searchParams.get('payment');
    if (payment === 'success') {
      setMsg('결제가 완료되어 이용권이 충전되었습니다.');
      loadBalance();
      setSearchParams({}, { replace: true });
    } else if (payment === 'fail') {
      setMsg(searchParams.get('message') || '결제가 취소되었습니다.');
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePurchase = async (packageType: AIPackageType) => {
    if (!isValidTool) return;
    setLoading(true);
    setMsg('');
    try {
      const cfg = await aiCreditsAPI.getConfig();
      if (!cfg.data.niceReady) {
        setMsg('결제 시스템이 아직 설정되지 않았습니다. 잠시 후 다시 시도해주세요.');
        return;
      }
      const order = await aiCreditsAPI.preparePurchase(toolType as AIToolType, packageType);
      if (!(window as any).AUTHNICE) {
        await new Promise<void>((resolve, reject) => {
          const s = document.createElement('script');
          s.src = 'https://pay.nicepay.co.kr/v1/js/';
          s.onload = () => resolve();
          s.onerror = () => reject(new Error('나이스페이 SDK 로드 실패'));
          document.head.appendChild(s);
        });
      }
      (window as any).AUTHNICE.requestPay({
        clientId: order.clientId,
        method: 'card',
        orderId: order.orderId,
        amount: order.amount,
        goodsName: order.goodsName,
        returnUrl: order.returnUrl,
      });
    } catch (e: any) {
      setMsg(e.response?.data?.message || '결제 준비 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  if (!isValidTool) {
    return <div style={S.page}><div style={S.container}>잘못된 접근입니다.</div></div>;
  }

  return (
    <div style={S.page}>
      <div style={S.container}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 14, marginBottom: 20 }}>
          <ArrowLeft size={18} /> 뒤로
        </button>

        <div style={S.card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <Zap size={20} color="#a78bfa" />
            <h1 style={{ fontSize: 19, fontWeight: 800, margin: 0 }}>{label} 이용권</h1>
          </div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 20 }}>
            현재 잔여 이용권: <strong style={{ color: '#a78bfa' }}>{balance ?? '-'}건</strong>
          </div>

          {msg && (
            <div style={{ padding: '10px 14px', background: 'rgba(167,139,250,0.1)', color: '#a78bfa', borderRadius: 9, fontSize: 13, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
              <CheckCircle size={14} /> {msg}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <button onClick={() => handlePurchase('single')} disabled={loading} style={{
              padding: '18px 12px', borderRadius: 12, border: '1px solid rgba(167,139,250,0.3)',
              background: 'rgba(167,139,250,0.06)', color: '#fff', cursor: loading ? 'not-allowed' : 'pointer', textAlign: 'center',
            }}>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 6 }}>1건</div>
              <div style={{ fontSize: 20, fontWeight: 800 }}>490원</div>
            </button>
            <button onClick={() => handlePurchase('bundle12')} disabled={loading} style={{
              padding: '18px 12px', borderRadius: 12, border: '1px solid #a78bfa',
              background: '#a78bfa', color: '#fff', cursor: loading ? 'not-allowed' : 'pointer', textAlign: 'center', position: 'relative',
            }}>
              <div style={{ position: 'absolute', top: -8, right: 8, fontSize: 10, background: '#fff', color: '#a78bfa', padding: '2px 6px', borderRadius: 6, fontWeight: 700 }}>15% 할인</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', marginBottom: 6 }}>12건 묶음</div>
              <div style={{ fontSize: 20, fontWeight: 800 }}>4,990원</div>
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 18, fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>
            <CreditCard size={12} /> 나이스페이먼츠로 안전하게 결제됩니다. 성사 여부와 무관한 도구 이용료이며, 이용권은 기능별로 별도 집계됩니다.
          </div>
        </div>
      </div>
    </div>
  );
};

export default AICreditsPage;
